/**
 * Create form for a holiday.
 */
import * as React from 'react';
import { Calendar, FileText } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { Input } from '@/components/forms/Input';
import { Select, type SelectOption } from '@/components/forms/Select';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DatePicker } from '@/components/forms/DatePicker';
import { Checkbox } from '@/components/forms/Checkbox';
import { createHoliday, getVacationZones } from '@/services/holidays.service';
import { getEstablishments } from '@/services/establishments.service';
import type { EstablishmentsResponse } from '@/types/establishment.types';
import type { CreateHolidayDto } from '@/types/holiday.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { holidayValidationSchema } from '@/utils/validation-schemas-utils';
import { HOLIDAY_FORM_QUERY_KEYS, HOLIDAY_TYPE_FORM_OPTIONS } from './holiday-configs';
import {
  applyHolidayFormField,
  applyHolidayTypeChange,
  buildCreateHolidayDto,
  emptyCreateHolidayForm,
  mapHolidayEstablishmentOptions,
  mapVacationZoneOptions,
} from './holiday-form-utils';

/** Props for {@link CreateHolidayView}. */
interface CreateHolidayViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/** Create form for a holiday. */
export const CreateHolidayView: React.FC<CreateHolidayViewProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = React.useState<CreateHolidayDto>(emptyCreateHolidayForm);

  const validation = useFormValidation(holidayValidationSchema);

  const { data: establishmentsResponse } = useQuery<EstablishmentsResponse>({
    queryKey: HOLIDAY_FORM_QUERY_KEYS.establishments,
    queryFn: () => getEstablishments(),
  });

  const { data: vacationZones = [] } = useQuery({
    queryKey: HOLIDAY_FORM_QUERY_KEYS.vacationZones,
    queryFn: () => getVacationZones(),
  });

  const vacationZoneOptions: SelectOption[] = React.useMemo(
    () => mapVacationZoneOptions(vacationZones),
    [vacationZones],
  );

  const establishmentOptions: SelectOption[] = React.useMemo(
    () => mapHolidayEstablishmentOptions(establishmentsResponse?.data),
    [establishmentsResponse],
  );

  const createMutation = useMutation({
    mutationFn: (dto: CreateHolidayDto) => createHoliday(dto),
    onSuccess: () => {
      toast.success('Vacances créées avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const isSubmitting = createMutation.isPending;

  /** Patch one field and re-validate. */
  const handleFieldChange = (field: keyof CreateHolidayDto, value: string | boolean) => {
    const updatedData = applyHolidayFormField(formData, field, value);
    setFormData(updatedData);
    validation.validateField(field, value, updatedData);
    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Changing type clears establishment or zone so outdated ids are not submitted. */
  const handleTypeChange = (value: string) => {
    const updatedData = applyHolidayTypeChange(formData, value);
    setFormData(updatedData);
    validation.validateField('type', value, updatedData);
    validation.markAsTouched('type');
    validation.markAsDirty('type');
  };

  /** Validate then POST create DTO (resolves establishment name when needed). */
  const handleSubmit = () => {
    if (!validation.validateForm(formData)) return;
    createMutation.mutate(buildCreateHolidayDto(formData, establishmentsResponse?.data));
  };

  return (
    <SlidePanelForm
      formType="create"
      title="Créer une période de vacances"
      subtitle="Renseignez les informations de la période de vacances"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={isSubmitting}
      isValid={validation.isFormValid()}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Nom"
            value={formData.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            placeholder="Ex: Vacances de Noël"
            maxLength={255}
            required
            helperText="Nom de la période de vacances ou férié"
            state={validation.getFieldState('name')}
            error={validation.getFieldError('name')}
          />

          <Select
            label="Type"
            value={formData.type}
            onChange={(value) => handleTypeChange(value)}
            onBlur={() => handleBlur('type')}
            options={HOLIDAY_TYPE_FORM_OPTIONS}
            required
            state={validation.getFieldState('type')}
            error={validation.getFieldError('type')}
          />
        </div>

        {formData.type === 'establishment_specific' && (
          <div className="animate-slide-down">
            <Autocomplete
              label="Établissement"
              value={formData.establishmentId || ''}
              onChange={(value) => handleFieldChange('establishmentId', value)}
              onBlur={() => handleBlur('establishmentId')}
              options={establishmentOptions}
              placeholder="Rechercher un établissement..."
              required
              state={validation.getFieldState('establishmentId')}
              error={validation.getFieldError('establishmentId')}
              minSearchLength={3}
            />
          </div>
        )}

        {formData.type === 'regional' && (
          <div className="animate-slide-down mt-4">
            <Select
              label="Zone vacances scolaires"
              value={formData.vacationZoneId || ''}
              onChange={(value) => handleFieldChange('vacationZoneId', value)}
              onBlur={() => handleBlur('vacationZoneId')}
              options={vacationZoneOptions}
              helperText="Optionnel : restreindre à une zone A, B ou C"
              state={validation.getFieldState('vacationZoneId')}
              error={validation.getFieldError('vacationZoneId')}
            />
          </div>
        )}

        <Input
          label="Description"
          value={formData.description || ''}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          onBlur={() => handleBlur('description')}
          placeholder="Ex: Vacances scolaires d'hiver"
          maxLength={500}
          state={validation.getFieldState('description')}
          error={validation.getFieldError('description')}
        />
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <Calendar className="w-4 h-4" />
          Dates
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DatePicker
            label="Date de début"
            value={formData.startDate}
            onChange={(value) => handleFieldChange('startDate', value)}
            onBlur={() => handleBlur('startDate')}
            required
            state={validation.getFieldState('startDate')}
            error={validation.getFieldError('startDate')}
          />

          <DatePicker
            label="Date de fin"
            value={formData.endDate}
            onChange={(value) => handleFieldChange('endDate', value)}
            onBlur={() => handleBlur('endDate')}
            minDate={formData.startDate}
            required
            state={validation.getFieldState('endDate')}
            error={validation.getFieldError('endDate')}
          />
        </div>

        <Checkbox
          label="Récurrent chaque année"
          checked={formData.isRecurringYearly || false}
          onCheckedChange={(checked) => handleFieldChange('isRecurringYearly', checked)}
        />
      </div>
    </SlidePanelForm>
  );
};
